// components/CalendarRegistration.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Download, Calendar, MapPin, Eye } from "lucide-react";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import '../CSS/LandingCalendarRegistration.css';

const PmpCalendarRegistration = ({ openEligibilityModal }) => {
  const [loading, setLoading] = useState(true);
  const [trainingSessions, setTrainingSessions] = useState([]);
  const [showPriceModal, setShowPriceModal] = useState(false);
  const navigate = useNavigate();
  const [selectedSession, setSelectedSession] = useState(null);

  // Données du tableau des tarifs
  const priceData = [
    {
      designation: "Offre PMP® Standard",
      note: "Hors frais d'Examen PMP",
      condition: "",
      price: "Nous contacter",
    },
    {
      designation: "Offre PMP® Sponsorisée*",
      note: "Hors frais d'Examen PMP",
      condition: "*Sous conditions ; Limitée",
      price: "Nous contacter",
    },
    {
      designation: "Frais d'examen de Certification PMP",
      note: "",
      condition: "",
      price: "Nous contacter",
    }
  ];

  // Fonction pour formater les dates comme "17, 18, 24, 25, 31 janvier 2026"
  const formatPeriodes = (periodes) => {
    if (!periodes || periodes.length === 0) return "";

    const sortedDates = [...periodes].sort((a, b) => new Date(a) - new Date(b));
    const datesByMonthYear = {};

    sortedDates.forEach(dateStr => {
      const date = new Date(dateStr);
      const monthYear = `${date.getMonth()}-${date.getFullYear()}`;

      if (!datesByMonthYear[monthYear]) {
        datesByMonthYear[monthYear] = [];
      }
      datesByMonthYear[monthYear].push(date.getDate());
    });

    const formattedPeriods = Object.keys(datesByMonthYear).map(monthYear => {
      const [monthIndex, year] = monthYear.split('-').map(Number);
      const days = datesByMonthYear[monthYear];

      const monthNames = [
        "janvier", "février", "mars", "avril", "mai", "juin",
        "juillet", "août", "septembre", "octobre", "novembre", "décembre"
      ];

      const formattedDays = days.join(', ');
      return `${formattedDays} ${monthNames[monthIndex]} ${year}`;
    });

    return formattedPeriods.join(' et ');
  };

  // Fonction pour formater la période avec mois et coaching
  const formatPeriodeComplete = (periodes, displayMonth, location, periodeCoaching) => {
    if (!periodes || periodes.length === 0) return "";

    const formationPeriod = formatPeriodes(periodes);

    if (periodeCoaching) {
      return `
      <div class="landing-periode-container">
        <div class="landing-periode-header">
          <h4 class="landing-periode-mois">${displayMonth}</h4>
          <div class="landing-periode-package">
            Formation + coaching + Examen-blanc
          </div>
        </div>
        <div class="landing-periode-details">
          <div class="landing-periode-line">
            <span class="landing-periode-label">Formation :&nbsp</span>
            <span class="landing-periode-value">${formationPeriod}</span>
          </div>
          <div class="landing-periode-line">
            <span class="landing-periode-label">Coaching :&nbsp</span>
            <span class="landing-periode-value">${periodeCoaching}</span>
          </div>
        </div>
      </div>
    `;
    }

    return `
    <div class="landing-periode-container">
      <div class="landing-periode-header">
        <h4 class="landing-periode-mois">${displayMonth}</h4>
        <div class="landing-periode-package">
            Formation + coaching + Examen-blanc
        </div>
      </div>
      <div class="landing-periode-details">
        <div class="landing-periode-line">
          <span class="landing-periode-label">Formation :&nbsp</span>
          <span class="landing-periode-value">${formationPeriod}</span>
        </div>
        <div class="landing-periode-line">
            <span class="landing-periode-label">Coaching :&nbsp</span>
            <span class="landing-periode-value">Aucun coaching</span>
          </div>
      </div>
    </div>
  `;
  };

  // Fonction pour vérifier si l'utilisateur a déjà fait le questionnaire
  const hasCompletedEligibility = () => {
    try {
      const eligibilityKeys = [
        'pmp_eligibility_score',
        'eligibility_score',
        'questionnaire_completed',
        'pmp_questionnaire_done'
      ];

      for (const key of eligibilityKeys) {
        if (localStorage.getItem(key)) {
          return true;
        }
      }
      return false;
    } catch (error) {
      console.error("Erreur lors de la vérification du localStorage:", error);
      return false;
    }
  };

  // Fonction pour gérer le clic sur le bouton "S'inscrire"
  const handleInscription = (formationReference, sessionData) => {
    if (!formationReference) {
      alert("Référence de formation manquante");
      return;
    }

    const hasEligibility = hasCompletedEligibility();

    if (hasEligibility) {
      navigate('/inscription', {
        state: {
          formationReference: formationReference,
          sessionData: sessionData,
          eligibilityCompleted: true,
          certificationType: "PMP"
        }
      });
    } else {
      // ❌ Pas éligible → ouvrir le modal
      // 🔥 STOCKER LA SESSION EN ATTENTE DANS LOCALSTORAGE
      localStorage.setItem('pending_calendar_session', JSON.stringify(sessionData));
      localStorage.setItem('pending_calendar_reference', formationReference);
      localStorage.setItem('pending_calendar_certification', 'PMP');

      if (openEligibilityModal && typeof openEligibilityModal === 'function') {
        openEligibilityModal();
      } else {
        navigate('/inscription', {
          state: {
            formationReference: formationReference,
            sessionData: sessionData,
            showEligibilityAlert: true,
            certificationType: "PMP"
          }
        });
      }
    }
  };

  // Fonction pour scroller vers la section contact
  const scrollToContact = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  // Fonction pour afficher/masquer le modal des tarifs
  const handleShowPrice = () => {
    let priceElement;

    if (window.innerWidth <= 768) {
      priceElement = document.querySelector('.landing-price-blocks-mobile');
    } else {
      priceElement = document.querySelector('.landing-price-table-desktop');
    }

    if (!priceElement) {
      priceElement = document.getElementById('tableau-prix');
    }

    if (priceElement) {
      priceElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });

      priceElement.style.transition = 'all 0.3s ease';
      priceElement.style.boxShadow = '0 0 20px rgba(10, 55, 100, 0.3)';

      setTimeout(() => {
        priceElement.style.boxShadow = '';
      }, 1000);
    }
  };

  const renderLocationList = (location) => {
    if (!location) return null;
    const parts = location.split(', ');
    return (
      <ul className="landing-lieu-list">
        {parts.map((part, i) => (
          <li key={i}>{part.trim()}</li>
        ))}
      </ul>
    );
  };

  // Récupérer les formations depuis l'API
  useEffect(() => {
    const fetchFormations = async () => {
      try {
        setLoading(true);
        const API_URL = process.env.REACT_APP_API_URL;

        const response = await fetch(`${API_URL}/formations-code/IVTPMP/`);

        if (!response.ok) {
          throw new Error(`Erreur HTTP: ${response.status}`);
        }

        const data = await response.json();

        if (data.success && data.formations && data.formations.length > 0) {
          const sessions = data.formations.map(formation => {
            let periods = [];
            try {
              if (formation.periode && formation.periode !== "[]") {
                const cleanedPeriode = formation.periode
                  .replace(/'/g, '"')
                  .replace(/\[/g, '[')
                  .replace(/\]/g, ']');
                periods = JSON.parse(cleanedPeriode);
              }
            } catch (e) {
              console.error("Erreur parsing période:", e);
              if (formation.date_debut) {
                periods = [formation.date_debut];
              }
            }

            if (periods.length === 0 && formation.date_debut) {
              periods = [formation.date_debut];
            }

            let displayMonth = "";
            let monthIndex = 0;
            let year = 0;

            if (periods.length > 0) {
              const firstDate = new Date(periods[0]);
              const monthNames = [
                "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
                "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"
              ];
              monthIndex = firstDate.getMonth();
              year = firstDate.getFullYear();
              displayMonth = `${monthNames[monthIndex]} ${year}`;
            }

            return {
              id: formation.id,
              formationId: formation.id,
              reference: formation.reference || "",
              title: formation.nom || "Formation PMP®",
              periods: periods,
              periodeFormatee: formatPeriodeComplete(
                periods,
                displayMonth,
                formation.lieu || "Cotonou",
                formation.periode_coaching || ""
              ),
              displayMonth: displayMonth,
              monthIndex: monthIndex,
              year: year,
              duration: "5 jours",
              location: formation.lieu || "Cotonou",
              seatsTotal: parseInt(formation.nb_participants) || 15,
              instructor: "Formateur expert",
              periodeCoaching: formation.periode_coaching || ""
            };
          }).filter(session => session.periods.length > 0);

          const sortedSessions = sessions.sort((a, b) => {
            if (a.year !== b.year) {
              return a.year - b.year;
            }
            if (a.monthIndex !== b.monthIndex) {
              return a.monthIndex - b.monthIndex;
            }
            const aFirstDate = new Date(a.periods[0]);
            const bFirstDate = new Date(b.periods[0]);
            return aFirstDate - bFirstDate;
          });

          setTrainingSessions(sortedSessions);
        } else {
          setTrainingSessions([]);
        }
      } catch (error) {
        console.error("Erreur détaillée:", error);
        setTrainingSessions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchFormations();
  }, []);

  // Effet pour gérer la redirection après questionnaire
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'pmp_eligibility_score' && e.newValue && selectedSession) {
        setTimeout(() => {
          navigate('/inscription', {
            state: {
              formationReference: selectedSession.reference,
              sessionData: selectedSession.data,
              certificationType: "PMP"
            }
          });
          setSelectedSession(null);
        }, 1000);
      }
    };

    window.addEventListener('storage', handleStorageChange);

    const checkEligibility = () => {
      if (hasCompletedEligibility() && selectedSession) {
        navigate('/inscription', {
          state: {
            formationReference: selectedSession.reference,
            sessionData: selectedSession.data,
            certificationType: "PMP"
          }
        });
        setSelectedSession(null);
      }
    };

    const interval = setInterval(checkEligibility, 1000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [selectedSession, navigate]);

  // Télécharger PDF du tableau
  const handleDownload = () => {
    try {
      const doc = new jsPDF();

      doc.setFontSize(20);
      doc.setTextColor(0, 0, 0);
      doc.setFont("helvetica", "bold");
      doc.text("Calendrier des Formations PMP<sup>®</sup> 2026", 105, 35, { align: "center" });

      const pdfData = trainingSessions.length > 0
        ? trainingSessions.map(session => {
          const formationPeriod = formatPeriodes(session.periods);
          let periodeText = "";

          if (session.periodeCoaching) {
            periodeText = `${session.displayMonth}\nFormation + coaching + Examen-blanc\nFormation : ${formationPeriod}\nCoaching : ${session.periodeCoaching}`;
          } else {
            periodeText = `${session.displayMonth}\nFormation : ${formationPeriod}`;
          }

          return [
            periodeText,
            session.location
          ];
        })
        : [];

      if (pdfData.length === 0) {
        doc.text("Aucune session disponible", 105, 60, { align: "center" });
      } else {
        const headers = [["Période", "Lieu"]];

        autoTable(doc, {
          head: headers,
          body: pdfData,
          startY: 50,
          theme: 'grid',
          headStyles: {
            fillColor: [10, 55, 100],
            textColor: 255,
            fontSize: 11,
            fontStyle: 'bold'
          },
          bodyStyles: {
            fontSize: 9,
            cellPadding: { top: 8, right: 5, bottom: 8, left: 5 },
            minCellHeight: 30
          },
          margin: { left: 15, right: 15 }
        });
      }

      doc.setFontSize(8);
      doc.setTextColor(100, 116, 139);
      doc.text("© Inovact - Tous droits réservés", 105, doc.internal.pageSize.height - 5, { align: "center" });

      const fileName = `Calendrier_PMP_2026.pdf`;
      doc.save(fileName);
    } catch (error) {
      console.error("Erreur lors de la génération du PDF:", error);
      alert("Une erreur est survenue lors de la génération du PDF");
    }
  };

  // Utiliser les données de l'API seulement
  const tableData = trainingSessions.map(session => ({
    mois: session.displayMonth,
    periode: session.periodeFormatee,
    lieu: session.location,
    action: "S'inscrire",
    reference: session.reference || `PMP-${session.displayMonth.replace(/\s+/g, '-')}-${session.location}`,
    sessionData: session
  }));

  if (loading) {
    return (
      <section className="landing-calendar-registration" id="calendrier">
        <div className="landing-contenir">
          <h2 className="landing-section-title">Calendrier des Formations PMP® 2026</h2>
          <p className="landing-section-subtitle">
            Consultez nos prochaines sessions de formation et inscrivez-vous dès maintenant.
          </p>
          <div className="landing-loading-spinner"></div>
        </div>
      </section>
    );
  }

  return (
    <section className="landing-calendar-registration" id="calendrier">
      <div className="landing-contenir">
        <h2 className="landing-section-title">Calendrier des Formations PMP<sup>®</sup> 2026</h2>
        <p className="landing-section-subtitle">
          Consultez nos prochaines sessions de formation et inscrivez-vous dès maintenant.
          Les places sont limitées pour garantir la qualité de l'accompagnement.
        </p>

        {/* Bouton de téléchargement */}
        <div className="landing-table-controls">
          <button onClick={handleDownload} className="landing-download-btn">
            <Download size={16} />
            Télécharger le calendrier
          </button>
        </div>

        {/* Afficher message si pas de sessions */}
        {tableData.length === 0 ? (
          <div className="landing-no-sessions-message">
            <div className="landing-alert landing-alert-info">
              <h4>⚠️ Aucune session disponible</h4>
              <p>Il n'y a pas de sessions de formation programmées pour le moment.</p>
              <p>Veuillez revenir ultérieurement ou nous contacter pour plus d'informations.</p>
            </div>
          </div>
        ) : (
          <>
            {/* TABLEAU EN GRID AVEC BLOCS POUR DESKTOP */}
            <div className="landing-grid-calendar-container">
              <div className="landing-grid-header landing-periode-header">
                <div className="landing-header-content">Période</div>
              </div>
              <div className="landing-grid-header landing-lieu-header">
                <div className="landing-header-content landing-centered">Lieu</div>
              </div>
              <div className="landing-grid-header landing-action-header">
                <div className="landing-header-content landing-centered">Action</div>
              </div>

              {tableData.map((row, index) => (
                <React.Fragment key={index}>
                  <div className={`landing-grid-cell landing-periode-cell ${index % 2 === 0 ? 'landing-row-even' : 'landing-row-odd'}`}>
                    <div className="landing-cell-content">
                      <div
                        className="landing-periode-value"
                        dangerouslySetInnerHTML={{ __html: row.periode }}
                      />
                    </div>
                  </div>

                  <div className={`landing-grid-cell landing-lieu-cell ${index % 2 === 0 ? 'landing-row-even' : 'landing-row-odd'}`}>
                    <div className="landing-cell-content">
                      <div className="landing-lieu-value">{renderLocationList(row.lieu)}</div>
                    </div>
                  </div>

                  <div className={`landing-grid-cell landing-action-cell ${index % 2 === 0 ? 'landing-row-even' : 'landing-row-odd'}`}>
                    <div className="landing-cell-content">
                      <div className="landing-action-buttons">
                        <button
                          className="landing-grid-inscription-btn"
                          onClick={() => handleInscription(row.reference, row.sessionData)}
                        >
                          {row.action}
                        </button>
                        <button
                          className="landing-grid-price-btn"
                          onClick={scrollToContact}
                        >
                          Tarif : contactez-nous !
                        </button>
                      </div>
                    </div>
                  </div>
                </React.Fragment>
              ))}
            </div>

            {/* VERSION MOBILE AMÉLIORÉE */}
            <div className="landing-mobile-calendar-improved">
              {tableData.map((row, index) => (
                <div key={index} className="landing-mobile-session-card">
                  <div className="landing-session-header">
                    <div className="landing-session-month">
                      <Calendar size={18} />
                      <span className="landing-month-text">{row.mois}</span>
                    </div>
                    <div className="landing-session-location">
                      <MapPin size={16} />
                      <span className="landing-location-text">{row.lieu}</span>
                    </div>
                  </div>

                  <div className="landing-session-dates">
                    <div className="landing-dates-label">Période :</div>
                    <div
                      className="landing-dates-value"
                      dangerouslySetInnerHTML={{ __html: row.periode }}
                    />
                  </div>

                  <div className="landing-session-footer">
                    <div className="landing-mobile-action-buttons">
                      <button
                        className="landing-mobile-inscription-btn"
                        onClick={() => handleInscription(row.reference, row.sessionData)}
                      >
                        {row.action}
                      </button>
                      <button
                        className="landing-mobile-price-btn"
                        onClick={scrollToContact}
                      >
                        Tarif : contactez-nous !
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <style jsx>{`
        /* Contrôles du tableau */
        .landing-table-controls {
          display: flex;
          justify-content: flex-end;
          margin-bottom: 2rem;
          margin-left: auto;
          margin-right: auto;
          padding-right: calc((100% - 1200px) / 2);
        }
        
        .landing-download-btn {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem;
          background: #0a3764;
          color: white;
          border: none;
          border-radius: 4px;
          font-size: 1rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        
        .landing-download-btn:hover {
          background: #09407e;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(10, 55, 100, 0.2);
        }
        
        /* Message aucune session */
        .landing-no-sessions-message {
          margin: 2rem 0;
          text-align:center;
        }
        
        .landing-no-sessions-message .landing-alert {
          border: 2px solid #0a3764;
          background-color: #f0f7ff;
        }
        
        .landing-no-sessions-message h4 {
          color: #0a3764;
          margin-bottom: 1rem;
        }
        
        .landing-no-sessions-message p {
          margin-bottom: 0.5rem;
        }
        
        /* GRID CALENDRIER AVEC BLOCS POUR DESKTOP - 3 COLONNES */
        .landing-grid-calendar-container {
          display: grid;
          grid-template-columns: 1.8fr 1.2fr 1.5fr;
          border: 2px solid #0a3764;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 6px 25px rgba(0, 0, 0, 0.1);
          margin: 0 auto 3rem auto;
          max-width: 1200px
        }
        
        /* En-têtes */
        .landing-grid-header {
          background: #0a3764;
          color: white;
          font-weight: 700;
          padding: 1.2rem 1.5rem;
          display: flex;
          align-items: center;
          border-right: 1px solid rgba(255, 255, 255, 0.2);
          font-size: 1.1rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .landing-centered {
          display: flex;
          justify-content: center;
          align-items: center;
          text-align: center;
          width: 100%;
        }
        
        .landing-grid-header:last-child {
          border-right: none;
        }
        
        /* Cellules de la grille */
        .landing-grid-cell {
          padding: 1.2rem 1.5rem;
          display: flex;
          align-items: center;
          border-bottom: 1px solid #e0e0e0;
          border-right: 1px solid #e0e0e0;
          min-height: 130px;
        }
        
        .landing-action-cell {
          border-right: none;
        }
        
        .landing-grid-cell:nth-last-child(-n+3) {
          border-bottom: none;
        }
        
        .landing-row-even {
          background-color: #ffffff;
        }
        
        .landing-row-odd {
          background-color: #f9fafb;
        }
        
        .landing-grid-cell:hover {
          background-color: #f0f7ff;
        }
        
        .landing-cell-content {
          width: 100%;
        }
        
        .landing-periode-cell .landing-cell-content {
          width: 100%;
        }
        
        .landing-periode-value {
          color: #333333;
          line-height: 1.6;
          font-size: 0.95rem;
        }
        
        /* Styles pour la période formatée */
        .landing-periode-container {
          width: 100%;
        }
        
        .landing-periode-mois {
          font-size: 1.1rem;
          font-weight: 700;
          color: #0a3764;
          margin: 0 0 0.5rem 0;
        }
        
        .landing-periode-package {
          font-size: 0.9rem;
          font-weight: 600;
          display: inline-block;
          margin-bottom: 0.5rem;
        }
        
        .landing-periode-details {
          background: #f8fafc;
          padding: 0.8rem;
          border-radius: 6px;
          border-left: 3px solid #0a3764;
        }
        
        .landing-periode-line {
          margin-bottom: 0.5rem;
          display: flex;
          align-items: flex-start;
        }
        
        .landing-periode-line:last-child {
          margin-bottom: 0;
        }
        
        .landing-periode-label {
          font-weight: 600;
          color: #0a3764;
          min-width: 80px;
          font-size: 0.9rem;
        }
        
        .landing-periode-value {
          flex: 1;
          color: #333;
          font-size: 0.9rem;
        }
        
        .landing-lieu-cell .landing-cell-content {
          display: flex;
          justify-content: center;
          align-items: center;
        }
        
        .landing-lieu-value {
          font-weight: 600;
          color: #1a365d;
          font-size: 1.1rem;
          text-align: center;
          min-width: 120px;
        }
        
        .landing-lieu-list {
          margin: 0;
          padding-left: 1.2rem;
          text-align: left;
        }
        
        .landing-lieu-list li {
          margin: 0.2rem 0;
        }
        
        .landing-action-cell .landing-cell-content {
          display: flex;
          justify-content: center;
          align-items: center;
        }
        
        .landing-action-buttons {
          display: flex;
          flex-direction: column;
          gap: 0.8rem;
          width: 100%;
          max-width: 215px;
        }
        
        .landing-grid-inscription-btn {
          padding: 0.75rem 1rem;
          background: linear-gradient(135deg, #94bf3e 0%, #7ea832 100%);
          color: white;
          border: none;
          border-radius: 6px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          font-size: 0.95rem;
          width: 100%;
          box-shadow: 0 3px 10px rgba(148, 191, 62, 0.3);
        }
        
        .landing-grid-inscription-btn:hover {
          background: linear-gradient(135deg, #83a935 0%, #6d922b 100%);
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(148, 191, 62, 0.4);
        }
        
        .landing-grid-price-btn {
          padding: 0.65rem 1rem;
          background: #f0f7ff;
          color: #0a3764;
          border: 1px solid #0a3764;
          border-radius: 6px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          font-size: 0.9rem;
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
        }
        
        .landing-grid-price-btn:hover {
          background: #e0efff;
          transform: translateY(-2px);
          box-shadow: 0 3px 10px rgba(10, 55, 100, 0.15);
        }
        
        /* VERSION MOBILE AMÉLIORÉE */
        .landing-mobile-calendar-improved {
          display: none;
        }
        
        .landing-mobile-session-card {
          background: white;
          border: 1px solid #e0e0e0;
          border-radius: 10px;
          padding: 1.2rem;
          margin-bottom: 1.2rem;
          box-shadow: 0 3px 10px rgba(0, 0, 0, 0.06);
          transition: all 0.3s ease;
        }
        
        .landing-mobile-session-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 6px 15px rgba(0, 0, 0, 0.1);
          border-color: #0a3764;
        }
        
        .landing-session-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
          padding-bottom: 0.8rem;
          border-bottom: 1px solid #eaeaea;
        }
        
        .landing-session-month, .landing-session-location {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        
        .landing-session-month svg {
          color: #0a3764;
        }
        
        .landing-month-text {
          font-size: 1.1rem;
          font-weight: 700;
          color: #0a3764;
        }
        
        .landing-session-location svg {
          color: #94bf3e;
        }
        
        .landing-location-text {
          font-size: 0.9rem;
          color: #666;
          font-weight: 500;
        }
        
        .landing-session-dates {
          margin-bottom: 1.2rem;
        }
        
        .landing-dates-label {
          font-size: 0.9rem;
          font-weight: 600;
          color: #666;
          margin-bottom: 0.5rem;
          display: none;
        }
        
        .landing-dates-value {
          font-size: 1rem;
          color: #333;
          line-height: 1.5;
          padding: 0.8rem;
          border-radius: 4px;
          background-color: #f8fafc;
          border-left: 3px solid #0a3764;
        }

        @media (max-width: 768px) {
          .landing-dates-value {
            background-color: transparent;
            border-left: none;
          }
        }
        
        /* Cacher le mois en double dans la période sur mobile */
        .landing-dates-value .landing-periode-mois {
          display: none;
        }
        
        .landing-dates-value .landing-periode-package {
          font-size: 0.85rem;
          color: #333;
          font-weight: 600;
          display: inline-block;
          margin-bottom: 0.8rem;
        }
        
        .landing-dates-value .landing-periode-details {
          padding: 0.5rem;
        }
        
        .landing-dates-value .landing-periode-line {
          margin-bottom: 0.4rem;
          font-size: 0.9rem;
        }
        
        .landing-dates-value .landing-periode-label {
          font-weight: 600;
          color: #0a3764;
          min-width: 70px;
          font-size: 0.85rem;
        }
        
        .landing-dates-value .landing-periode-value {
          font-size: 0.85rem;
        }
        
        .landing-session-footer {
          display: flex;
          justify-content: center;
        }
        
        .landing-mobile-action-buttons {
          display: flex;
          gap: 1rem;
          width: 100%;
          max-width: 400px;
        }
        
        .landing-mobile-inscription-btn {
          padding: 0.8rem 1.5rem;
          background: linear-gradient(135deg, #94bf3e 0%, #7ea832 100%);
          color: white;
          border: none;
          border-radius: 6px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          font-size: 0.95rem;
          flex: 1;
          box-shadow: 0 3px 10px rgba(148, 191, 62, 0.3);
        }
        
        .landing-mobile-inscription-btn:hover {
          background: linear-gradient(135deg, #83a935 0%, #6d922b 100%);
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(148, 191, 62, 0.4);
        }
        
        .landing-mobile-price-btn {
          padding: 0.8rem 1.5rem;
          background: #f0f7ff;
          color: #0a3764;
          border: 1px solid #0a3764;
          border-radius: 6px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          font-size: 0.95rem;
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
        }
        
        .landing-mobile-price-btn:hover {
          background: #e0efff;
          transform: translateY(-2px);
          box-shadow: 0 3px 10px rgba(10, 55, 100, 0.15);
        }
        
        /* Responsive pour tablette */
        @media (max-width: 1024px) {
          .landing-grid-header {
            padding: 1rem 1.2rem;
            font-size: 1rem;
          }
          
          .landing-grid-cell {
            padding: 1rem 1.2rem;
            min-height: 120px;
          }
          
          .landing-periode-mois {
            font-size: 1rem;
          }
          
          .landing-periode-package {
            font-size: 0.85rem;
          }
          
          .landing-periode-label, .landing-periode-value {
            font-size: 0.85rem;
          }
          
          .landing-lieu-value {
            font-size: 1rem;
            min-width: 100px;
          }
          
          .landing-grid-inscription-btn, .landing-grid-price-btn {
            padding: 0.6rem 0.8rem;
            font-size: 0.9rem;
          }
        }
        
        /* Responsive pour mobile */
        @media (max-width: 768px) {
          .landing-section-title {
            font-size: 2rem;
          }
          
          .landing-section-subtitle {
            font-size: 1rem;
          }
          
          .landing-table-controls {
            justify-content: center;
            padding-right: 0;
          }
          
          .landing-download-btn {
            font-size: 0.95rem;
            padding: 0.6rem 1.2rem;
          }
          
          .landing-grid-calendar-container {
            display: none !important;
          }
          
          .landing-mobile-calendar-improved {
            display: block;
            margin-bottom: 2rem;
          }
          
          .landing-mobile-action-buttons {
            flex-direction: column;
            gap: 0.8rem;
          }
          
          .landing-mobile-session-card {
            padding: 1rem;
          }
          
          .landing-month-text {
            font-size: 1rem;
          }
          
          .landing-location-text {
            font-size: 0.85rem;
          }
          
          .landing-dates-value {
            font-size: 0.9rem;
            padding: 0.6rem;
          }
          
          .landing-mobile-inscription-btn,
          .landing-mobile-price-btn {
            padding: 0.7rem 1rem;
            font-size: 0.9rem;
          }
        }
        
        @media (max-width: 480px) {
          .landing-mobile-session-card {
            padding: 0.8rem;
          }
          
          .landing-month-text {
            font-size: 0.95rem;
          }
          
          .landing-location-text {
            font-size: 0.8rem;
          }
          
          .landing-dates-value {
            font-size: 0.85rem;
            padding: 0.5rem;
          }
          
          .landing-mobile-inscription-btn,
          .landing-mobile-price-btn {
            padding: 0.6rem 0.8rem;
            font-size: 0.85rem;
          }
        }
        
        @media (max-width: 360px) {
          .landing-session-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.5rem;
          }
          
          .landing-session-month, .landing-session-location {
            width: 100%;
          }
          
          .landing-mobile-session-card {
            padding: 0.8rem;
          }
          
          .landing-month-text {
            font-size: 0.95rem;
          }
          
          .landing-location-text {
            font-size: 0.8rem;
          }
          
          .landing-dates-value {
            font-size: 0.85rem;
            padding: 0.5rem;
          }
          
          .landing-mobile-inscription-btn,
          .landing-mobile-price-btn {
            padding: 0.6rem 0.8rem;
            font-size: 0.85rem;
          }
        }
        
        /* Desktop - afficher tableau, cacher mobile */
        @media (min-width: 769px) {
          .landing-grid-calendar-container {
            display: grid !important;
          }
          
          .landing-mobile-calendar-improved {
            display: none !important;
          }
        }
      `}</style>
    </section>
  );
};

export default PmpCalendarRegistration;